import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { Textarea } from '@/components/ui/textarea';
import { ChecklistLibrary } from './ChecklistLibrary';
import { P, usePermissions } from '@/features/permissions';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Item = {
  id: string;
  label: string;
  status: string;
  owner: string;
  due_date: string;
  note: string;
  version: number;
  evidence: { document_id: string; quote: string }[];
  history: {
    at: string;
    action: string;
    actor_id: string;
    before?: Record<string, string>;
    after?: Record<string, string>;
  }[];
};
type Template = { version: number; id: string; name: string; scope: string; labels: string[] };
export function PatientChecklist({ patientId }: { patientId: string }) {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const editable = can(P.UPDATE_PATIENT);
  const base = `/api/v1/organizations/${organization?.organization_id}/coordination`;
  const cache = useQueryClient();
  const [newLabel, setNewLabel] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [labels, setLabels] = useState(
    'Referral received\nDemographics confirmed\nRecords reviewed\nAppointment scheduled',
  );
  const [scope, setScope] = useState('personal');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const query = useQuery({
    queryKey: ['patient-checklist', base, patientId],
    queryFn: async () => (await clientApi.get<{ items: Item[]; draft: boolean }>(`${base}/patients/${patientId}`)).data,
  });
  const templates = useQuery({
    queryKey: ['checklist-templates', base],
    queryFn: async () => (await clientApi.get<Template[]>(`${base}/templates`)).data,
  });
  const change = useMutation({
    mutationFn: async ({ path, body, method = 'post' }: { path: string; body: unknown; method?: 'post' | 'put' }) =>
      clientApi[method](`${base}${path}`, body),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ['patient-checklist'] });
      cache.invalidateQueries({ queryKey: ['checklist-templates'] });
      cache.invalidateQueries({ queryKey: ['patients'] });
    },
  });
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Patient checklist</CardTitle>
          <CardDescription>
            Coordinate work between your team and the agent. Every saved change is recorded.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {query.isPending && <p role="status">Loading checklist…</p>}
          {query.isError && (
            <p role="alert">
              Checklist unavailable.{' '}
              <Button variant="outline" onClick={() => query.refetch()}>
                Retry
              </Button>
            </p>
          )}
          {query.data?.draft && (
            <Alert>
              <AlertTitle>Draft patient — demographics unconfirmed</AlertTitle>
              <AlertDescription>
                Review and correct the demographics in the chart before confirming this patient.
                {editable && (
                  <Button
                    variant="outline"
                    disabled={change.isPending}
                    onClick={() => change.mutate({ body: {}, path: `/patients/${patientId}/confirm` })}>
                    Confirm reviewed demographics
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}
          {change.isError && <p role="alert">The change was not saved. Check your permissions and try again.</p>}
          {query.data && !query.data.items.length && <p>No tasks yet. Add a task or apply a template.</p>}
          {query.data?.items.map((item) => (
            <ChecklistRow
              key={`${item.id}:${item.version}`}
              item={item}
              editable={editable}
              base={`${base}/patients/${patientId}`}
            />
          ))}
          {editable && (
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="new-task">Add a patient-specific task</FieldLabel>
                <Input id="new-task" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
              </Field>
              <Button
                disabled={!newLabel.trim() || change.isPending}
                onClick={() =>
                  change.mutate(
                    { body: { label: newLabel }, path: `/patients/${patientId}` },
                    { onSuccess: () => setNewLabel('') },
                  )
                }>
                Add task
              </Button>
            </FieldGroup>
          )}
        </CardContent>
      </Card>
      {editable && query.data && (
        <ChecklistLibrary
          base={base}
          patientId={patientId}
          existing={query.data.items.map((item) => item.label)}
          onTemplate={(selection) => {
            setLabels(selection.join('\n'));
            setEditingTemplate(null);
            setTemplateName('');
            setTemplateOpen(true);
          }}
        />
      )}
      {editable && (
        <Card>
          <CardHeader>
            <CardTitle>Checklist templates</CardTitle>
            <CardDescription>Apply a shared or personal template, then customize this patient’s tasks.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {templates.isError && <p role="alert">Templates could not be loaded.</p>}
            {templates.data?.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-3">
                <span>
                  {t.name} · {t.scope}
                </span>
                <Button
                  variant="outline"
                  disabled={change.isPending}
                  onClick={() => change.mutate({ body: {}, path: `/patients/${patientId}/templates/${t.id}` })}>
                  Apply template
                </Button>
                {(t.scope === 'personal' || can('manage_organization')) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingTemplate(t);
                      setTemplateName(t.name);
                      setLabels(t.labels.join('\n'));
                      setScope(t.scope);
                      setTemplateOpen(true);
                    }}>
                    Edit template
                  </Button>
                )}
                <details>
                  <summary>Preview tasks</summary>
                  {t.labels.map((label, i) => (
                    <p key={i}>{label}</p>
                  ))}
                </details>
              </div>
            ))}
            <details open={templateOpen} onToggle={(e) => setTemplateOpen(e.currentTarget.open)}>
              <summary>{editingTemplate ? 'Edit template' : 'Create a template'}</summary>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="template-name">Template name</FieldLabel>
                  <Input id="template-name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="template-labels">Tasks, one per line</FieldLabel>
                  <Textarea id="template-labels" rows={6} value={labels} onChange={(e) => setLabels(e.target.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="template-scope">Available to</FieldLabel>
                  <select
                    id="template-scope"
                    value={scope}
                    disabled={!!editingTemplate}
                    onChange={(e) => setScope(e.target.value)}>
                    <option value="personal">Only me</option>
                    {can('manage_organization') && <option value="organization">Organization</option>}
                  </select>
                </Field>
                <Button
                  disabled={!templateName.trim() || !labels.trim() || change.isPending}
                  onClick={() =>
                    change.mutate(
                      {
                        method: editingTemplate ? 'put' : 'post',
                        path: editingTemplate ? `/templates/${editingTemplate.id}` : '/templates',
                        body: {
                          scope,
                          name: templateName,
                          labels: labels.split('\n').filter((x) => x.trim()),
                          ...(editingTemplate ? { expected_version: editingTemplate.version } : {}),
                        },
                      },
                      {
                        onSuccess: () => {
                          setTemplateName('');
                          setEditingTemplate(null);
                          setTemplateOpen(false);
                        },
                      },
                    )
                  }>
                  Save template
                </Button>
                {editingTemplate && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingTemplate(null);
                      setTemplateName('');
                    }}>
                    Save as a new template instead
                  </Button>
                )}
              </FieldGroup>
            </details>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
function ChecklistRow({ item, base, editable }: { item: Item; base: string; editable: boolean }) {
  const [value, setValue] = useState({
    note: item.note,
    label: item.label,
    owner: item.owner,
    status: item.status,
    due_date: item.due_date,
  });
  const cache = useQueryClient();
  const save = useMutation({
    onSuccess: () => cache.invalidateQueries({ queryKey: ['patient-checklist'] }),
    mutationFn: () => clientApi.put(`${base}/${item.id}`, { ...value, expected_version: item.version }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{item.label}</CardTitle>
        <CardDescription>
          {item.status.replaceAll('_', ' ')}
          {item.owner && ` · ${item.owner}`}
          {item.due_date && ` · Due ${item.due_date}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <details>
          <summary>{editable ? 'Edit task' : 'Task details'}</summary>
          <FieldGroup>
            {(['label', 'owner', 'due_date', 'note'] as const).map((key) => (
              <Field key={key}>
                <FieldLabel htmlFor={`${item.id}-${key}`}>
                  {{ label: 'Task', note: 'Notes', due_date: 'Due date', owner: 'Responsible person or agent' }[key]}
                </FieldLabel>
                {key === 'note' ? (
                  <Textarea
                    id={`${item.id}-${key}`}
                    value={value[key]}
                    disabled={!editable}
                    onChange={(e) => setValue({ ...value, [key]: e.target.value })}
                  />
                ) : (
                  <Input
                    id={`${item.id}-${key}`}
                    value={value[key]}
                    disabled={!editable}
                    type={key === 'due_date' ? 'date' : 'text'}
                    onChange={(e) => setValue({ ...value, [key]: e.target.value })}
                  />
                )}
              </Field>
            ))}
            <Field>
              <FieldLabel htmlFor={`${item.id}-status`}>Status</FieldLabel>
              <select
                id={`${item.id}-status`}
                disabled={!editable}
                value={value.status}
                onChange={(e) => setValue({ ...value, status: e.target.value })}>
                {['open', 'in_progress', 'completed', 'not_applicable'].map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
          </FieldGroup>
          {save.isError && (
            <p role="alert">Task was not saved. It may have changed; reload the checklist before retrying.</p>
          )}
          {editable && (
            <Button variant="outline" disabled={save.isPending || !value.label.trim()} onClick={() => save.mutate()}>
              Save task
            </Button>
          )}
        </details>
        {!!item.evidence.length && (
          <details>
            <summary>Source evidence</summary>
            {item.evidence.map((e, i) => (
              <blockquote key={i} className="whitespace-pre-wrap">
                {e.quote}
                <p>Document: {e.document_id}</p>
              </blockquote>
            ))}
          </details>
        )}
        <details>
          <summary>Change history ({item.history.length})</summary>
          {item.history.map((h, i) => (
            <div key={i}>
              <p>
                {new Date(h.at).toLocaleString()} · {h.action.replaceAll('_', ' ')} · {h.actor_id}
              </p>
              {h.after &&
                Object.entries(h.after).map(([key, v]) => (
                  <p key={key}>
                    {key}: {h.before?.[key] || '—'} → {v || '—'}
                  </p>
                ))}
            </div>
          ))}
        </details>
      </CardContent>
    </Card>
  );
}
